import { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Download, X, Share } from 'lucide-react';

/** Front-facing (marketing) pages where the install nudge may appear. It is
 *  intentionally kept off the authenticated in-app routes. */
const isPublicPath = (pathname) =>
  !(/^\/(app|subscription|onboarding|owner-bootstrap|therapist-intake|therapist-apply|therapist-manage|directory|therapists|login|register|forgot-password|assessment)(\/|$)/.test(pathname)
    || pathname === '/assessment/results' || pathname === '/reset-password' || pathname === '/verify-email');

const DISMISS_KEY = 'bp_install_dismissed';

const PUBLIC_BASE = (process.env.REACT_APP_PUBLIC_URL || 'https://brotherhoodplus.com').replace(/\/$/, '');
const DEFAULT_TITLE = "Brotherhood Plus (Brotherhood+) | Men's Wellness App";
const DEFAULT_IMAGE = `${PUBLIC_BASE}/og-image.png`;

/** Per-route SEO overrides for public pages (title, description, canonical, social preview). */
const ROUTE_SEO = {
  '/': {
    title: "Brotherhood Plus (Brotherhood+) | Men's Wellness App",
    description: 'Brotherhood Plus (Brotherhood+) is a men\u2019s emotional wellness app \u2014 created with men of color in mind. Daily emotional check-ins, guided and voice journaling, AI wellness support, and a directory of culturally responsive therapists. Start a 7-day free trial.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Brotherhood+ \u2014 a men\u2019s emotional wellness app.',
  },
  '/features': {
    title: 'Features | Brotherhood+ Men\u2019s Wellness App',
    description: 'Explore Brotherhood+ features: daily emotional check-ins, guided and voice journaling, AI-powered wellness support, learning resources, and a free therapist directory \u2014 built for men\u2019s mental wellness.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Brotherhood+ features for men\u2019s emotional wellness.',
  },
  '/about': {
    title: 'About Brotherhood+ | Emotional Wellness for Men',
    description: 'Brotherhood+ is an emotional and mental wellness app created with men of color in mind, open to every man. Learn our mission, who it\u2019s for, and how wellness support differs from therapy.',
    image: DEFAULT_IMAGE,
    imageAlt: 'About Brotherhood+ \u2014 emotional wellness for men.',
  },
  '/faq': {
    title: 'FAQ | Brotherhood+ Men\u2019s Wellness App',
    description: 'Answers about Brotherhood+: what it is, who it\u2019s for, how your journals and check-ins stay private, pricing, the 7-day free trial, and finding a therapist.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Brotherhood+ frequently asked questions.',
  },
  '/directory': {
    title: 'Find a Therapist \u2014 Brotherhood+ Directory',
    description: 'Browse a free, vetted directory of culturally responsive, licensed therapists for men of every background. Filter by state, specialty, insurance, and language \u2014 no account needed.',
    image: `${PUBLIC_BASE}/og-directory.png`,
    imageAlt: 'Brotherhood+ \u2014 Find a Therapist: culturally responsive care.',
  },
  '/crisis': {
    title: 'Crisis & Support Resources | Brotherhood+',
    description: 'Immediate crisis and mental-health support resources and hotlines. If you or someone you love is struggling, help is available right now.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Brotherhood+ crisis and support resources.',
  },
  '/support': {
    title: 'Help & Contact | Brotherhood+',
    description: 'Get help with your Brotherhood+ account and membership, or contact our team at brotherhoodplusapp@gmail.com.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Brotherhood+ help and contact.',
  },
  '/privacy': {
    title: 'Privacy Policy | Brotherhood+',
    description: 'How Brotherhood+ collects, uses, and protects your data. Your journals, check-ins, and coach conversations stay private.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Brotherhood+ privacy policy.',
  },
  '/terms': {
    title: 'Terms of Service | Brotherhood+',
    description: 'The terms that govern your use of Brotherhood+, including membership, billing, and acceptable use.',
    image: DEFAULT_IMAGE,
    imageAlt: 'Brotherhood+ terms of service.',
  },
  '/therapist-apply': {
    title: 'List Your Practice \u2014 Brotherhood+ Therapist Directory',
    description: 'Are you a licensed, culturally responsive therapist? Join the free Brotherhood+ directory and reach men looking for care that understands their context.',
    image: `${PUBLIC_BASE}/og-directory.png`,
    imageAlt: 'Join the Brotherhood+ therapist directory.',
  },
};

function _setTag(selector, content) {
  const el = document.head.querySelector(selector);
  if (!el) return;
  if (!el.hasAttribute('data-seo-orig')) el.setAttribute('data-seo-orig', el.getAttribute('content') ?? el.getAttribute('href') ?? '');
  if (el.tagName === 'LINK') el.setAttribute('href', content); else el.setAttribute('content', content);
}
function _restoreTags() {
  document.head.querySelectorAll('[data-seo-orig]').forEach((el) => {
    const orig = el.getAttribute('data-seo-orig');
    if (el.tagName === 'LINK') el.setAttribute('href', orig); else el.setAttribute('content', orig);
    el.removeAttribute('data-seo-orig');
  });
}

/** Toggles a runtime `noindex` on authenticated / private routes, and applies
 *  per-route SEO overrides for public pages. Additive — no feature change. */
export function SeoRouteMeta() {
  const { pathname } = useLocation();
  useEffect(() => {
    const priv = /^\/(app|subscription|onboarding|owner-bootstrap|login|register|forgot-password|reset-password|verify-email|assessment|therapist-intake|therapist-manage)(\/|$)/.test(pathname);
    // Preview/staging safeguard driven by a BUILD-TIME signal (the backend URL
    // baked into the bundle), NOT the live render host. This is critical because
    // bot/prerender layers may render the production bundle on an internal or
    // preview-like host; a hostname check would then wrongly inject noindex into
    // the snapshot served to crawlers. The baked REACT_APP_BACKEND_URL is fixed
    // per build, so production is always indexable and only preview builds are
    // excluded. Unknown/empty defaults to INDEXABLE (fail-safe for production).
    const backendUrl = (process.env.REACT_APP_BACKEND_URL || '').toLowerCase();
    const isPreviewBuild = /(localhost|127\.0\.0\.1|\.preview\.|emergentagent\.com|emergent\.host)/.test(backendUrl);
    const noindex = priv || isPreviewBuild;
    let tag = document.querySelector('meta[name="robots"][data-dynamic="1"]');
    if (noindex) {
      if (!tag) {
        tag = document.createElement('meta');
        tag.setAttribute('name', 'robots');
        tag.setAttribute('data-dynamic', '1');
        document.head.appendChild(tag);
      }
      tag.setAttribute('content', 'noindex, nofollow');
    } else if (tag) {
      tag.remove();
    }

    // Per-route SEO overrides (updates the static index.html tags; restores them elsewhere).
    const seo = ROUTE_SEO[pathname];
    if (seo) {
      const url = `${PUBLIC_BASE}${pathname}`;
      document.title = seo.title;
      _setTag('meta[name="description"]', seo.description);
      _setTag('meta[property="og:title"]', seo.title);
      _setTag('meta[property="og:description"]', seo.description);
      _setTag('meta[property="og:url"]', url);
      _setTag('meta[property="og:image"]', seo.image);
      _setTag('meta[property="og:image:alt"]', seo.imageAlt);
      _setTag('meta[name="twitter:title"]', seo.title);
      _setTag('meta[name="twitter:description"]', seo.description);
      _setTag('meta[name="twitter:image"]', seo.image);
      _setTag('link[rel="canonical"]', url);
    } else {
      document.title = DEFAULT_TITLE;
      _restoreTags();
    }
  }, [pathname]);
  return null;
}

const isIos = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent || '');
const isStandalone = () =>
  window.matchMedia?.('(display-mode: standalone)').matches || window.navigator.standalone === true;

/** Subtle, dismiss-once "Install app" nudge. Uses the native install prompt on
 *  Chrome/Edge/Android; shows Share → Add to Home Screen steps on iOS Safari.
 *  Never re-appears after dismissal. Purely optional — adds no app feature. */
export default function InstallPrompt() {
  const { pathname } = useLocation();
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHelp, setIosHelp] = useState(false);

  const dismiss = useCallback(() => {
    setShow(false); setIosHelp(false);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    if (isStandalone()) return;                       // already installed → never show
    try { if (localStorage.getItem(DISMISS_KEY) === '1') return; } catch { /* ignore */ }
    if (!isPublicPath(pathname)) return;              // front-facing pages only

    // Capture the native prompt when the browser offers it (Chrome/Edge/Android).
    const onBip = (e) => { e.preventDefault(); setDeferred(e); setShow(true); };
    window.addEventListener('beforeinstallprompt', onBip);
    // The moment the app is installed, remove the nudge for good.
    const onInstalled = () => dismiss();
    window.addEventListener('appinstalled', onInstalled);

    // Universal fallback so the message reliably appears on the front page even
    // when `beforeinstallprompt` never fires (iOS Safari, already-eligible, etc.).
    const t = setTimeout(() => { if (!isStandalone()) setShow(true); }, isIos() ? 4000 : 2500);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
      clearTimeout(t);
    };
  }, [pathname, dismiss]);

  const install = async () => {
    if (deferred) {
      deferred.prompt();
      try { await deferred.userChoice; } catch { /* ignore */ }
      setDeferred(null);
      dismiss();
    } else if (isIos()) {
      setIosHelp(true);
    } else {
      // No native prompt available (e.g. desktop that isn't eligible) → show manual steps.
      setIosHelp(true);
    }
  };

  if (!show) return null;

  return (
    <div
      role="dialog"
      aria-label="Install Brotherhood+"
      data-testid="install-prompt"
      className="install-toast fixed left-1/2 -translate-x-1/2 z-[55] w-[calc(100%-2rem)] max-w-md"
    >
      <div className="bg-navy text-white rounded-2xl shadow-xl border border-white/10 p-4">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl bg-white/10 flex items-center justify-center shrink-0">
            <Download size={18} className="text-gold" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-sm">Add Brotherhood+ to your Home Screen</p>
            {iosHelp ? (
              isIos() ? (
                <p className="text-xs text-white/70 mt-1 leading-relaxed inline-flex flex-wrap items-center gap-1">
                  In <span className="font-semibold">Safari</span>, tap <Share size={12} className="inline text-gold" /> Share, then <span className="font-semibold">Add to Home Screen</span>.
                </p>
              ) : (
                <p className="text-xs text-white/70 mt-1 leading-relaxed">
                  Open your browser menu (⋮) and choose <span className="font-semibold">Install app</span> or <span className="font-semibold">Add to Home Screen</span>.
                </p>
              )
            ) : (
              <p className="text-xs text-white/70 mt-1 leading-relaxed">
                {isIos()
                  ? 'In Safari, add it to your Home Screen — full screen, one tap away.'
                  : 'Launch it like an app — full screen, one tap away.'}
              </p>
            )}
            {!iosHelp && (
              <div className="flex items-center gap-2 mt-3">
                <button
                  onClick={install}
                  data-testid="install-prompt-cta"
                  className="text-xs font-semibold bg-gold text-navy rounded-full px-4 py-2 hover:opacity-90 transition-smooth"
                >
                  {deferred ? 'Install app' : (isIos() ? 'How to install (Safari)' : 'How to install')}
                </button>
                <button onClick={dismiss} data-testid="install-prompt-dismiss" className="text-xs text-white/60 px-2 py-2 hover:text-white transition-smooth">
                  Not now
                </button>
              </div>
            )}
          </div>
          <button onClick={dismiss} aria-label="Dismiss" className="text-white/50 hover:text-white transition-smooth -mt-1 -mr-1 p-1">
            <X size={16} />
          </button>
        </div>
      </div>
    </div>
  );
}
